import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api/client';
import { usePrintHeader } from '../../hooks/usePrintHeader';
import { useSettings } from '../../hooks/useSettings';
import { addressLine, date, fine, money, payWords, rate, testingWords, weight } from '../../utils/format';
import { ErrorNotice, Loading } from '../../components/ui';
import { Breakup, PrintDocument, PrintToolbar } from '../../components/print/PrintDocument';

interface PurchaseLine {
  line_no: number;
  description: string | null;
  product_name: string | null;
  metal_name: string | null;
  purity_code: string;
  declared_purity_code: string | null;
  gross_wt: string;
  stone_wt: string | null;
  other_wt: string | null;
  net_wt: string;
  fine_wt: string;
  melting_loss_wt: string;
  payable_fine_wt: string;
  rate_value: string;
  deduction_amount: string | null;
  line_total: string;
  calc_input: { meltingLossPct?: string; itemCode?: string } | null;
}

interface Purchase {
  doc_no: string;
  doc_date: string;
  purchase_type: string;
  status: string;
  party_name: string;
  party_phone: string | null;
  party_gstin: string | null;
  party_pan: string | null;
  party_address: unknown;
  branch_name?: string;
  testing_method: string | null;
  tested_by_name: string | null;
  id_proof_type: string | null;
  id_proof_last4: string | null;
  supplier_invoice_no: string | null;
  supplier_invoice_date: string | null;
  settlement: string | null;
  paid_by: string | null;
  advance_used?: string | null;
  advance_left?: string | null;
  taxable_value: string;
  tax_total: string;
  round_off: string;
  grand_total: string;
  lines: PurchaseLine[];
}

const n = (v: string | null | undefined): number => Number(v || 0);
const sum = (lines: PurchaseLine[], key: keyof PurchaseLine): string =>
  lines.reduce((a, l) => a + n(l[key] as string), 0).toFixed(4);

/**
 * The old gold receipt — the customer's proof of what they handed over — and,
 * for a supplier, the purchase voucher.
 *
 * The receipt shows every step from the weight on the scale to the money paid:
 * what came off for stones and dirt, the purity they declared beside the purity
 * the shop tested, and the melting loss deducted. A receipt that shows only the
 * final figure is the one that starts arguments a fortnight later.
 */
export function OldGoldPrintPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const { data, error, isLoading } = useQuery({
    queryKey: ['purchase', id],
    queryFn: () => api.get<Purchase>(`/purchases/${id}`),
  });
  const { header } = usePrintHeader(data?.branch_name);
  const { setting } = useSettings();

  if (isLoading) return <Loading what="Preparing the receipt" />;
  if (error) return <ErrorNotice error={error} />;
  if (!data) return null;

  const isOldGold = data.purchase_type === 'OLD_GOLD_CUSTOMER';
  const lines = data.lines ?? [];
  const cancelled = data.status === 'CANCELLED' || data.status === 'REVERSED';
  const party = {
    name: data.party_name,
    phone: data.party_phone,
    gstin: data.party_gstin,
    pan: isOldGold ? data.party_pan : null,
    address: addressLine(data.party_address) || null,
  };

  return (
    <>
      <PrintToolbar
        onBack={() => navigate(isOldGold ? '/old-gold?tab=receipts' : '/purchases')}
        fileName={data.doc_no}
      />
      {isOldGold ? (
        <PrintDocument
          title={cancelled ? 'Old gold receipt (cancelled)' : 'Old gold purchase receipt'}
          header={header}
          docNo={data.doc_no}
          docDate={date(data.doc_date)}
          party={party}
          partyLabel="Bought from"
          details={[
            {
              label: 'Purity tested by',
              value: testingWords(data.testing_method).replace(/^./, (c) => c.toUpperCase()),
            },
            ...(data.tested_by_name ? [{ label: 'Tested by', value: data.tested_by_name }] : []),
            {
              label: 'ID proof seen',
              value: data.id_proof_type
                ? `${data.id_proof_type}${data.id_proof_last4 ? ` ending ${data.id_proof_last4}` : ''}`
                : '',
            },
            { label: 'Paid by', value: payWords(data.paid_by) },
          ]}
          totals={[
            { label: 'Gross weight', value: weight(sum(lines, 'gross_wt')) },
            { label: 'Net weight', value: weight(sum(lines, 'net_wt')) },
            { label: 'Fine weight paid on', value: fine(sum(lines, 'payable_fine_wt')), strong: true },
            ...(n(data.round_off) !== 0 ? [{ label: 'Round off', value: money(data.round_off) }] : []),
          ]}
          grandTotal={data.grand_total}
          grandTotalLabel={data.paid_by === 'KEPT_AS_ADVANCE' ? 'Kept as advance' : 'Amount paid'}
          footNote={[
            `Purity was tested by ${testingWords(data.testing_method)} in the customer’s presence, and the amount is paid on the tested purity.`,
            data.paid_by === 'KEPT_AS_ADVANCE'
              ? `The amount is kept as your advance with the shop, to use on a purchase. Advance held for you now: ${money(data.advance_left ?? '0')}.`
              : '',
            setting('documents.old_gold_declaration'),
          ]
            .filter(Boolean)
            .join('\n')}
          signatures={['Seller’s signature', `For ${header.companyName}`]}
        >
          <table className="doc-table">
            <thead>
              <tr>
                <th className="w-6">#</th>
                <th>Item</th>
                <th className="r">Gross</th>
                <th className="r">Net</th>
                <th className="c">Purity</th>
                <th className="r">Paid on</th>
                <th className="r">Rate / g</th>
                <th className="r">Amount</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const lossPct = l.calc_input?.meltingLossPct;
                const steps = [
                  n(l.stone_wt) > 0 ? `Less stones ${weight(l.stone_wt)}` : null,
                  n(l.other_wt) > 0 ? `Less dirt / lac ${weight(l.other_wt)}` : null,
                  l.declared_purity_code && l.declared_purity_code !== l.purity_code
                    ? `Customer said ${l.declared_purity_code}`
                    : null,
                  `Fine at ${l.purity_code}: ${fine(l.fine_wt)}`,
                  n(l.melting_loss_wt) > 0
                    ? `Less melting loss${lossPct ? ` ${Number(lossPct)}%` : ''}: ${fine(l.melting_loss_wt)}`
                    : null,
                  n(l.deduction_amount) > 0 ? `Less deduction ${money(l.deduction_amount)}` : null,
                ].filter(Boolean);
                return (
                  <tr key={l.line_no}>
                    <td className="num !text-left">{l.line_no}</td>
                    <td>
                      <span className="font-medium">{l.description ?? 'Old metal'}</span>
                      {l.metal_name ? <span className="text-ink-soft"> · {l.metal_name}</span> : null}
                      <Breakup parts={steps as string[]} />
                    </td>
                    <td className="num">{weight(l.gross_wt)}</td>
                    <td className="num">{weight(l.net_wt)}</td>
                    <td className="c num whitespace-nowrap font-semibold">{l.purity_code}</td>
                    <td className="num font-medium">{fine(l.payable_fine_wt)}</td>
                    <td className="num">{rate(l.rate_value)}</td>
                    <td className="num font-semibold">{money(l.line_total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </PrintDocument>
      ) : (
        <PrintDocument
          title={cancelled ? 'Purchase voucher (cancelled)' : 'Purchase voucher'}
          header={header}
          docNo={data.doc_no}
          docDate={date(data.doc_date)}
          party={party}
          partyLabel="Supplier"
          details={[
            { label: 'Their invoice', value: data.supplier_invoice_no ?? '' },
            { label: 'Invoice date', value: data.supplier_invoice_date ? date(data.supplier_invoice_date) : '' },
            { label: 'Pieces', value: String(lines.length) },
            {
              label: 'Settlement',
              value:
                n(data.advance_used) > 0
                  ? n(data.advance_used) >= n(data.grand_total)
                    ? 'From advance'
                    : `From advance, rest ${payWords(data.settlement ?? data.paid_by).toLowerCase()}`
                  : payWords(data.settlement ?? data.paid_by),
            },
            ...(n(data.advance_used) > 0
              ? [
                  { label: 'Advance adjusted', value: money(data.advance_used!) },
                  {
                    label: data.settlement === 'CREDIT' ? 'Still to pay' : 'Paid now',
                    value: money(Math.max(0, n(data.grand_total) - n(data.advance_used)).toFixed(2)),
                  },
                ]
              : []),
          ]}
          totals={[
            { label: 'Fine weight', value: fine(sum(lines, 'fine_wt')) },
            { label: 'Taxable value', value: money(data.taxable_value) },
            ...(n(data.tax_total) > 0 ? [{ label: 'GST', value: money(data.tax_total) }] : []),
            ...(n(data.round_off) !== 0 ? [{ label: 'Round off', value: money(data.round_off) }] : []),
          ]}
          grandTotal={data.grand_total}
          footNote={
            n(data.advance_used) > 0
              ? `Advance adjusted on this purchase: ${money(data.advance_used!)}. ${
                  n(data.grand_total) - n(data.advance_used) > 0
                    ? `${data.settlement === 'CREDIT' ? 'Still to pay' : 'Paid now'}: ${money((n(data.grand_total) - n(data.advance_used)).toFixed(2))}. `
                    : ''
                }Advance still with the supplier: ${money(data.advance_left ?? '0')}.`
              : n(data.advance_left) > 0
                ? `Advance with the supplier: ${money(data.advance_left!)}.`
                : undefined
          }
          signatures={['Checked and received by', `For ${header.companyName}`]}
        >
          <table className="doc-table">
            <thead>
              <tr>
                <th className="w-6">#</th>
                <th>Item</th>
                <th className="c">Purity</th>
                <th className="r">Gross</th>
                <th className="r">Net</th>
                <th className="r">Fine</th>
                <th className="r">Cost / fine g</th>
                <th className="r">Amount</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const code = l.calc_input?.itemCode ?? l.description;
                return (
                  <tr key={l.line_no}>
                    <td className="num !text-left">{l.line_no}</td>
                    <td>
                      <span className="font-medium">{l.product_name ?? code ?? 'Item'}</span>
                      {l.product_name && code ? <span className="breakup font-mono">{code}</span> : null}
                    </td>
                    <td className="c num">{l.purity_code}</td>
                    <td className="num">{weight(l.gross_wt)}</td>
                    <td className="num">{weight(l.net_wt)}</td>
                    <td className="num">{fine(l.fine_wt)}</td>
                    <td className="num">{rate(l.rate_value)}</td>
                    <td className="num font-semibold">{money(l.line_total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </PrintDocument>
      )}
    </>
  );
}
